import { useQueries } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { listRecordings } from '../playback/api';
import { searchDeviceRecordings, searchErrorLabels } from '../playback/deviceApi';
import { coverage, toSegs, type Span } from '../playback/ranges';
import { addDays, dayOf, dayRange } from '../playback/time';
import { Timeline } from '../playback/Timeline';
import { zoomLevels, type ZoomId } from '../playback/zoom';
import { nextRecorded, unionSpans, type ReplaySource } from './sync';

interface Props {
  /** The cameras of the mosaic, by id, with their names for the notes. */
  cameras: { id: string; name: string }[];
  /** Day shown (YYYY-MM-DD, organisation zone). */
  day: string;
  onDay: (day: string) => void;
  /** Replay clock, or null while live. */
  playhead: number | null;
  timeZone: string | undefined;
  /** A click on a track: replay every camera from t, from that origin. */
  onSeek: (t: number, origin: ReplaySource) => void;
}

/**
 * The day's recordings of the whole mosaic, on two tracks: what the platform
 * kept (Servidor) and what the DVR/NVR keeps (Equipamento). A moment is
 * covered when at least one camera recorded it.
 */
export function MosaicTimeline({ cameras, day, onDay, playhead, timeZone, onSeek }: Props) {
  const { request } = useAuth();
  const [zoom, setZoom] = useState<ZoomId>('1h');
  // "Now" moves the live mark and turns the day over at midnight.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  const today = dayOf(now, timeZone);
  const range = dayRange(day, timeZone);
  const [from, to] = range;
  const isToday = day === today;

  const server = useQueries({
    queries: cameras.map((c) => ({
      queryKey: ['wall-day-recordings', c.id, from],
      queryFn: () => listRecordings(request, c.id, from, to),
      retry: false,
      // Today keeps growing: new segments close every minute.
      refetchInterval: isToday ? 60_000 : (false as const),
    })),
  });
  // Searching asks each device itself: kept for a while, renewed slowly today.
  const device = useQueries({
    queries: cameras.map((c) => ({
      queryKey: ['wall-day-device-recordings', c.id, from],
      queryFn: () => searchDeviceRecordings(request, c.id, from, to),
      retry: false,
      staleTime: 5 * 60_000,
      refetchInterval: isToday ? 5 * 60_000 : (false as const),
    })),
  });

  const serverSpans = server.map((q) => coverage(toSegs(q.data ?? [])));
  const deviceSpans = device.map((q) =>
    q.data?.ok
      ? q.data.spans
          .map((s) => ({ start: Date.parse(s.from), end: Date.parse(s.to) }))
          .filter((s) => s.end > s.start)
      : [],
  );
  // Why a camera has no device track, by name (shown on hover).
  const deviceIssues = cameras.flatMap((c, i) => {
    const q = device[i];
    if (!q) return [];
    if (q.isError) {
      const unsupported = q.error instanceof ApiError && q.error.status === 409;
      return [
        `${c.name}: ${unsupported ? 'não oferece as gravações dele' : 'não foi possível buscar'}`,
      ];
    }
    if (q.data && !q.data.ok) {
      return [`${c.name}: ${searchErrorLabels[q.data.error_code ?? ''] ?? 'recusou a busca'}`];
    }
    return [];
  });

  const head = playhead ?? (isToday ? now : null);
  const serverTrack = unionSpans(serverSpans);
  const deviceTrack = unionSpans(deviceSpans);
  // A click in a gap plays the next recording; after the last one of today
  // there is only the present.
  const pick = (spans: Span[], origin: ReplaySource) => (t: number) => {
    const at = nextRecorded(spans, t);
    if (at !== null) onSeek(at, origin);
    else if (isToday) onSeek(Date.now(), origin);
  };

  return (
    <section aria-label="Gravações do mosaico" className="flex flex-col gap-1 px-3 pt-2 pb-1">
      <div className="flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
        <span className="font-medium text-fg-muted">Gravações</span>
        <div className="flex items-center gap-0.5">
          <DayButton
            label="Dia anterior"
            onClick={() => {
              onDay(addDays(day, -1));
            }}
          >
            <ChevronLeft aria-hidden="true" className="size-3.5" />
          </DayButton>
          <span className="min-w-[4.5rem] text-center font-mono text-fg tabular-nums">
            {isToday ? 'Hoje' : dayLabel(day)}
          </span>
          <DayButton
            label="Dia seguinte"
            disabled={isToday}
            onClick={() => {
              onDay(addDays(day, 1));
            }}
          >
            <ChevronRight aria-hidden="true" className="size-3.5" />
          </DayButton>
        </div>
        <div role="group" aria-label="Zoom da linha do tempo" className="ml-auto flex gap-0.5">
          {zoomLevels.map((z) => (
            <button
              key={z.id}
              type="button"
              aria-pressed={zoom === z.id}
              onClick={() => {
                setZoom(z.id);
              }}
              className={`h-6 rounded px-1.5 text-2xs font-medium transition-colors ${
                zoom === z.id
                  ? 'bg-accent-soft text-accent'
                  : 'text-fg-muted hover:bg-surface-hover hover:text-fg'
              }`}
            >
              {z.label}
            </button>
          ))}
        </div>
      </div>

      <Track
        title="Servidor"
        hint="Gravações guardadas no Pro Guard"
        spans={serverTrack}
        note={note(
          server.some((q) => q.isPending),
          serverSpans.filter((s) => s.length > 0).length,
          cameras.length,
          server.filter((q) => q.isError).length,
        )}
        day={range}
        playhead={head}
        zoom={zoom}
        timeZone={timeZone}
        ticks={false}
        onSeek={pick(serverTrack, 'server')}
      />
      <Track
        title="Equipamento"
        hint="Gravações dentro do DVR/NVR, em velocidade normal"
        spans={deviceTrack}
        note={note(
          device.some((q) => q.isPending),
          deviceSpans.filter((s) => s.length > 0).length,
          cameras.length,
          deviceIssues.length,
        )}
        issues={deviceIssues}
        day={range}
        playhead={head}
        zoom={zoom}
        timeZone={timeZone}
        ticks
        onSeek={(t) => {
          // Devices that skipped the search but replay any time over RTSP
          // (ADR-041): the track is empty, the click still plays from there.
          if (deviceTrack.length === 0 && device.some((q) => q.data?.direct_replay === true)) {
            onSeek(Math.min(t, Date.now() - 5_000), 'device');
          } else pick(deviceTrack, 'device')(t);
        }}
      />
    </section>
  );
}

/** "2 de 4 câmeras", with the cameras that could not answer. */
function note(pending: boolean, recorded: number, total: number, failed: number): string {
  if (pending) return 'Buscando…';
  const base = `${String(recorded)} de ${String(total)} câmera${total === 1 ? '' : 's'}`;
  return failed > 0 ? `${base} · ${String(failed)} sem resposta` : base;
}

function Track({
  title,
  hint,
  spans,
  note: text,
  issues = [],
  day,
  playhead,
  zoom,
  timeZone,
  ticks,
  onSeek,
}: {
  title: string;
  hint: string;
  spans: Span[];
  note: string;
  issues?: string[];
  day: [number, number];
  playhead: number | null;
  zoom: ZoomId;
  timeZone: string | undefined;
  ticks: boolean;
  onSeek: (t: number) => void;
}) {
  return (
    <div className="grid grid-cols-[5rem_minmax(0,1fr)] items-start gap-x-2 md:grid-cols-[6.5rem_minmax(0,1fr)_9rem] md:gap-2">
      <span title={hint} className="truncate pt-1 text-xs font-medium text-fg-muted">
        {title}
      </span>
      <Timeline
        day={day}
        spans={spans}
        playhead={playhead}
        zoom={zoom}
        timeZone={timeZone}
        onSeek={onSeek}
        label={`Gravações do mosaico: ${title}`}
        ticks={ticks}
        compact
      />
      <span
        title={issues.length > 0 ? issues.join('\n') : undefined}
        className={`col-start-2 truncate text-2xs md:col-start-auto md:pt-1 ${issues.length > 0 ? 'text-warn' : 'text-fg-subtle'}`}
      >
        {text}
      </span>
    </div>
  );
}

function DayButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-6 place-items-center rounded text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** DD/MM/AAAA of a YYYY-MM-DD day. */
function dayLabel(day: string): string {
  const [y, m, d] = day.split('-');
  return `${d ?? ''}/${m ?? ''}/${y ?? ''}`;
}
